import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Clipboard } from '@angular/cdk/clipboard'
import { HttpResponse } from '@angular/common/http'
import { EMPTY, Observable } from 'rxjs'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'
import { CallerService } from './caller.service'
import { AnalyticsService } from './analytics.service'
import { LeaveReviewComponent } from '../dialogs/leave-review/leave-review.component'
import { LoginSignupPromptComponent } from '../dialogs/login-signup-prompt/login-signup-prompt.component'
import { PpaModel, PpaReviewStats, ReviewModel } from '../ngrx-store/app.state'
import { ppaPlace, ppaUrl } from '../utils/ppa.util'

/** What POST /ppa/review answers: the review (redacted if anonymous), and the PPA's numbers with it counted. */
export interface PostedPpaReview {
  review: ReviewModel | null
  ppa_stats: PpaReviewStats | null
}

/** What you can do with a PPA - review it, share it - for the PPA card and the PPA page (/ppa/:id). */
@Injectable({ providedIn: 'root' })
export class PpaActionsService {
  constructor(
    private callerService: CallerService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private analyticsService: AnalyticsService,
  ) {}

  /**
   * Asks for a review (the dialog), then posts it. Emits once it's in - nothing if they cancel, it fails (they're
   * told why: "You've already reviewed this PPA 5 times"), or they aren't logged in (they're asked to). `onPosting`
   * runs once the dialog closes with a review, as it's sent.
   */
  review(ppa: PpaModel, onPosting?: () => void): Observable<PostedPpaReview> {
    if (!this.callerService.isLoggedIn) {
      this.dialog.open(LoginSignupPromptComponent, { maxWidth: '360px', data: {} })
      return EMPTY
    }
    return this.dialog
      .open(LeaveReviewComponent, { maxWidth: '360px', data: { ppa } })
      .afterClosed()
      .pipe(
        // Cancelled, or nothing filled in
        filter((review: any) => !!(review?.comment || review?.star_rating)),
        switchMap((review: any) => {
          onPosting?.() // the dialog closed with a review - it's on its way
          return this.callerService.addNewPpaReview(review)
        }),
        map((res: HttpResponse<any>) => {
          if (!res.ok) throw res
          return { review: res.body?.review ?? null, ppa_stats: res.body?.ppa_stats ?? null }
        }),
        tap(() => {
          this.analyticsService.ppaReviewed()
          this.callerService.showNotification('Thanks - your review is up.')
        }),
        catchError((err) => {
          this.callerService.showNotification(err?.error?.message ?? 'Couldn\'t post your review. Please try again.')
          return EMPTY
        }),
      )
  }

  /** The phone's share sheet where there is one, else the link is copied. */
  async share(ppa: PpaModel): Promise<void> {
    const url = ppaUrl(ppa)
    const where = ppaPlace(ppa.Locations?.[0])
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: ppa.name, text: `${ppa.name}${where ? `, ${where}` : ''} - PPA reviews on corpers.ng`, url })
      } catch (err) {
        // AbortError: they closed the sheet. Anything else, fall back to copying.
        if ((err as DOMException)?.name !== 'AbortError') this.copyLink(url)
      }
      return
    }
    this.copyLink(url)
  }

  private copyLink(url: string): void {
    const copied = this.clipboard.copy(url)
    this.callerService.showNotification(copied ? 'Link copied - paste it anywhere to share.' : 'Couldn\'t copy the link. Please try again.')
  }
}
